import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import { BookOpen, CheckCircle2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { ChapterReportView, SubjectInfo } from '@/types/chapterReport'
import { CorrectRateMetric, ScoreRing, UnderstandingMetric } from './ReportMetrics'

function curriculumLabel(subject: SubjectInfo): string {
  const gradePath = [
    subject.grade,
    subject.semester,
    subject.unit,
  ].filter((value): value is number => value != null).join('-')
  const prefix = [subject.school, gradePath].filter(Boolean).join(' ')
  return [
    prefix ? `[${prefix}]` : null,
    subject.unitName,
  ].filter(Boolean).join(' ') || subject.raw || ''
}

function formatDate(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value.slice(0, 10)
  return new Intl.DateTimeFormat('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date).replace(/\.$/, '')
}

export function ChapterLearningSection({ report }: { report: ChapterReportView }) {
  const { t } = useTranslation('coaching')
  const missing = t('chapterReport.missing')

  return (
    <section className="space-y-6" data-testid="chapter-learning-section">
      <div className="space-y-5">
        <div className="px-2">
          <p className="text-base font-semibold leading-[18px] text-[#4599ff]">
            {t('chapterReport.chapterLabel', {
              chapter: String(report.chapter.displayNumber ?? report.chapter.ordinal).padStart(2, '0'),
            })}
          </p>
          <h1 className="mt-1 text-2xl font-bold leading-[1.7] tracking-[-0.6px] text-[#131313]">
            <ContentMetadataText text={report.chapter.title} />
          </h1>
        </div>
        <div className="space-y-4 rounded-xl bg-[#f5f5f5] px-5 py-4">
          {report.chapter.learningObjectives ? (
            <div className="flex items-start gap-4">
              <h2 className="flex w-[88px] shrink-0 items-center gap-1 text-xs font-semibold leading-[1.4] text-[#4599ff]">
                <CheckCircle2 size={14} aria-hidden="true" />
                {t('chapterReport.learningContent')}
              </h2>
              <p className="whitespace-pre-wrap text-xs leading-[1.7] text-[#333]">
                {report.chapter.learningObjectives}
              </p>
            </div>
          ) : null}
          <div className="flex items-start gap-4">
            <h2 className="flex w-[88px] shrink-0 items-center gap-1 text-xs font-semibold leading-[1.4] text-[#4599ff]">
              <BookOpen size={14} aria-hidden="true" />
              {t('chapterReport.relatedCurriculum')}
            </h2>
            <div className="flex flex-wrap gap-2">
              {report.relatedCurriculum.length === 0 ? (
                <span className="text-xs text-[#888]">{t('chapterReport.relatedCurriculumEmpty')}</span>
              ) : (
                report.relatedCurriculum.map((subject, index) => (
                  <span
                    key={`${subject.raw ?? ''}-${index}`}
                    className="rounded-lg border border-[#d0d0d0] bg-white px-2.5 py-2 text-xs text-[#333]"
                  >
                    {curriculumLabel(subject)}
                  </span>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between px-2">
        <div>
          <p className="text-base font-bold leading-[1.4] text-[#131313]">
            {t('chapterReport.achievementLead')}
          </p>
          <p className="text-xl font-bold leading-[1.4] tracking-[-0.6px] text-[#131313]">
            {t('chapterReport.achievementResultBefore')}
            <span className="text-[#4599ff]">
              {report.initialAverageCorrectRate == null
                ? missing
                : `${report.initialAverageCorrectRate}%`}
            </span>
            {t('chapterReport.achievementResultAfter')}
          </p>
          <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#eee] px-2.5 py-1 text-xs">
            <span className="font-semibold text-[#888]">{t('chapterReport.learningPeriod')}</span>
            <span className="font-medium text-[#333]">
              {formatDate(report.period.startedAt)} ~ {formatDate(report.period.endedAt)}
            </span>
          </div>
        </div>
        <div className="flex w-[104px] justify-center">
          <ScoreRing
            value={report.initialAverageCorrectRate}
            ariaLabel={t('chapterReport.achievementAria', {
              value: report.initialAverageCorrectRate ?? missing,
            })}
          />
        </div>
      </div>

      <div className="space-y-2">
        <div className="overflow-hidden rounded-xl border border-[#eaeaea]">
          <table className="w-full table-fixed text-left text-xs">
            <thead className="bg-[#f5f5f5] text-[#888]">
              <tr>
                <th className="w-12 py-3 pl-4 text-center font-medium">{t('chapterReport.lessonNumber')}</th>
                <th className="px-4 py-3 font-medium">{t('chapterReport.lessonTitle')}</th>
                <th className="w-20 py-3 text-center font-medium">{t('chapterReport.initialUnderstanding')}</th>
                <th className="w-28 py-3 pr-6 text-center font-medium">{t('chapterReport.initialCorrectRate')}</th>
              </tr>
            </thead>
            <tbody>
              {report.progressLessons.map((lesson, index) => (
                <tr
                  key={lesson.lessonId}
                  className={`chapter-report-row border-t border-[#f5f5f5] ${index % 2 ? 'bg-[#f5f5f5]' : 'bg-white'}`}
                >
                  <td className="py-3 pl-4 text-center font-semibold text-[#888]">{lesson.lessonNumber}</td>
                  <td className="px-4 py-3">
                    <p className="font-semibold text-[#131313]"><ContentMetadataText text={lesson.title} /></p>
                    <p className="mt-1 leading-[1.4] text-[#333]"><ContentMetadataText text={lesson.keyPoint || missing} /></p>
                  </td>
                  <td className="py-3">
                    <UnderstandingMetric value={lesson.initialUnderstanding} />
                  </td>
                  <td className="py-3 pr-6">
                    <CorrectRateMetric value={lesson.initialCorrectRate} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-2 text-xs leading-[1.5] text-[#888]">
          <p>{t('chapterReport.understandingFootnote')}</p>
          <p>{t('chapterReport.correctRateFootnote')}</p>
        </div>
      </div>
    </section>
  )
}
